import React, { useState, useEffect, useCallback, useRef } from 'react';
import { bankTransactionsApi, studentsApi, configApi } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CurrencySelector from '../components/CurrencySelector';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MultiSelect } from '../components/MultiSelect';
import { Plus, Link2, AlertCircle, Loader2, Search, ChevronLeft, ChevronRight, Lightbulb, DollarSign, Clock, CheckCircle, Upload, FileSpreadsheet, Pencil, Lock } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { useBulkSelect, BulkDeleteButton, BulkSelectionBar } from '../components/BulkDelete';
import InvoiceMappingEditor from '../components/InvoiceMappingEditor';
import BankSelect from '../components/BankSelect';
import { toast } from 'sonner';

const formatCurrency = (amount, currency = 'UGX') => {
  if (!amount && amount !== 0) return '-';
  return `${currency} ${Number(amount).toLocaleString()}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const BankReconciliationPage = () => {
  const { isAdmin, role } = useAuth();
  // accountant_assistant has add-only access: it can add bank transactions but not
  // reconcile / move-to-suspense / import (those stay admin/accountant/manager).
  const canReconcile = isAdmin || ['accountant', 'manager'].includes(role);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const sel = useBulkSelect();
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${sel.size} selected bank transaction(s)? Any payments already created from reconciliation are kept. This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await bankTransactionsApi.bulkDelete(Array.from(sel.selected));
      toast.success(res.data.message || 'Deleted');
      sel.clear();
      fetchTransactions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setBulkDeleting(false);
    }
  };
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState([]);
  // Admin-only: branch-locked users are scoped by the API and never see the picker.
  const [branchFilter, setBranchFilter] = useState([]);
  const [config, setConfig] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showReconcileDialog, setShowReconcileDialog] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentResults, setStudentResults] = useState([]);
  const [searchingStudent, setSearchingStudent] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [summary, setSummary] = useState(null);
  const [matchSuggestions, setMatchSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Which branch's statement an admin is importing (locked users import into theirs).
  const [importBranch, setImportBranch] = useState('');
  const fileInputRef = useRef(null);
  
  // Split reconciliation state
  const [splits, setSplits] = useState([]);
  const [splitMode, setSplitMode] = useState(false);

  const [newTxn, setNewTxn] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    currency: 'USD',
    reference_number: '',
    bank_name: '',
    description: '',
    branch: ''
  });

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (statusFilter.length) params.status = statusFilter.join(',');
      if (branchFilter.length) params.branch = branchFilter.join(',');
      const response = await bankTransactionsApi.getAll(params);
      setTransactions(response.data.transactions);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (err) {
      toast.error('Failed to load transactions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, branchFilter]);

  const fetchSummary = async () => {
    try {
      const response = await api.get('/bank-transactions/summary');
      setSummary(response.data);
    } catch (err) {
      console.error('Failed to load summary', err);
    }
  };

  const fetchMatchSuggestions = async (txnId) => {
    setLoadingSuggestions(true);
    setMatchSuggestions([]);
    try {
      const response = await api.get(`/bank-transactions/match-suggestions/${txnId}`);
      setMatchSuggestions(response.data.suggestions || []);
    } catch (err) {
      console.error('Failed to load suggestions', err);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Separate effect for summary to ensure it runs on mount
  useEffect(() => {
    fetchSummary();
  }, []);

  // Branch list for the admin filter and the admin's add/import branch picker.
  useEffect(() => {
    configApi.get().then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const searchStudents = async (query) => {
    if (!query || query.length < 2) {
      setStudentResults([]);
      return;
    }
    setSearchingStudent(true);
    try {
      const response = await studentsApi.getAll({ search: query, limit: 10 });
      setStudentResults(response.data.students);
    } catch (err) {
      console.error(err);
    } finally {
      setSearchingStudent(false);
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setStudentResults([]);
    setStudentSearch('');
  };

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await bankTransactionsApi.create({
        date: newTxn.date,
        amount: parseFloat(newTxn.amount),
        currency: newTxn.currency,
        reference_number: newTxn.reference_number,
        bank_name: newTxn.bank_name,
        description: newTxn.description || null,
        // Locked users have this filled in server-side from their own branch.
        branch: newTxn.branch || null
      });
      toast.success('Bank transaction added');
      setShowAddDialog(false);
      setNewTxn({ date: new Date().toISOString().split('T')[0], amount: '', currency: 'USD', reference_number: '', bank_name: '', description: '', branch: '' });
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add transaction');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReconcile = async () => {
    if (splitMode) {
      // Split reconciliation
      if (splits.length === 0) { toast.error('Add at least one student split'); return; }
      const totalSplit = splits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
      if (Math.abs(totalSplit - selectedTxn.amount) > 0.01) {
        toast.error(`Split total (${totalSplit.toFixed(2)}) must equal transaction amount (${selectedTxn.amount.toFixed(2)})`);
        return;
      }
      setSubmitting(true);
      try {
        await bankTransactionsApi.reconcile(selectedTxn.id, { splits: splits.map(s => ({ student_id: s.student.id, amount: parseFloat(s.amount) })) });
        toast.success(`Transaction split across ${splits.length} students`);
        setShowReconcileDialog(false);
        resetReconcileState();
        fetchTransactions();
        fetchSummary();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to reconcile');
      } finally {
        setSubmitting(false);
      }
    } else {
      // Single student
      if (!selectedStudent) { toast.error('Please select a student'); return; }
      setSubmitting(true);
      try {
        await bankTransactionsApi.reconcile(selectedTxn.id, { student_id: selectedStudent.id });
        toast.success('Transaction reconciled and payment recorded');
        setShowReconcileDialog(false);
        resetReconcileState();
        fetchTransactions();
        fetchSummary();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to reconcile');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const resetReconcileState = () => {
    setSelectedTxn(null);
    setSelectedStudent(null);
    setMatchSuggestions([]);
    setSplits([]);
    setSplitMode(false);
  };

  const addSplit = (student) => {
    if (splits.find(s => s.student.id === student.id)) {
      toast.error('Student already added');
      return;
    }
    setSplits(prev => [...prev, { student, amount: '' }]);
    setStudentResults([]);
    setStudentSearch('');
  };

  const updateSplitAmount = (idx, amount) => {
    setSplits(prev => prev.map((s, i) => i === idx ? { ...s, amount } : s));
  };

  const removeSplit = (idx) => {
    setSplits(prev => prev.filter((_, i) => i !== idx));
  };

  const splitTotal = splits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
  const splitRemaining = selectedTxn ? selectedTxn.amount - splitTotal : 0;

  const handleMoveToSuspense = async (txnId) => {
    try {
      await bankTransactionsApi.moveToSuspense(txnId);
      toast.success('Transaction moved to suspense');
      fetchTransactions();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move to suspense');
    }
  };

  const openReconcileDialog = (txn) => {
    setSelectedTxn(txn);
    setShowReconcileDialog(true);
    fetchMatchSuggestions(txn.id);
  };

  // Edit bank transaction state
  const [editBT, setEditBT] = useState(null);
  const [editBTData, setEditBTData] = useState({});
  const [editBTSubmitting, setEditBTSubmitting] = useState(false);
  // Payment(s) this statement line was reconciled into — reconciliation records the
  // money as a payment, so its invoice mapping is what gets re-marked here.
  const [btPayments, setBtPayments] = useState([]);
  const [btPaymentsLoading, setBtPaymentsLoading] = useState(false);

  const fetchBTPayments = async (txnId) => {
    setBtPaymentsLoading(true);
    setBtPayments([]);
    try {
      const res = await bankTransactionsApi.getInvoiceMapping(txnId);
      setBtPayments(res.data.payments || []);
    } catch (err) {
      console.error('Failed to load invoice mapping', err);
    } finally {
      setBtPaymentsLoading(false);
    }
  };

  const handleEditBTSave = async () => {
    if (!editBT) return;
    setEditBTSubmitting(true);
    try {
      await api.put(`/bank-transactions/${editBT.id}`, editBTData);
      toast.success('Transaction updated');
      setEditBT(null);
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setEditBTSubmitting(false);
    }
  };

  const openBTEdit = (txn) => {
    setEditBT(txn);
    fetchBTPayments(txn.id);
    setEditBTData({
      date: txn.date || '',
      amount: txn.amount,
      currency: txn.currency || 'UGX',
      reference_number: txn.reference_number || '',
      bank_name: txn.bank_name || '',
      description: txn.description || ''
    });
  };

  const handleExcelUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    
    if (!allowedTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
      toast.error('Please upload an Excel (.xlsx, .xls) or CSV file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    // The whole statement belongs to one branch; an admin must say which, a
    // branch-locked user's own is applied server-side.
    if (isAdmin) {
      if (!importBranch) {
        toast.error('Pick the branch this statement belongs to before importing');
        setUploading(false);
        event.target.value = '';
        return;
      }
      formData.append('branch', importBranch);
    }

    try {
      const response = await api.post('/bank-transactions/import-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      
      fetchTransactions();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import Excel file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'reconciled':
        return <Badge className="bg-[#5556A8]/10 text-[#5556A8]">Reconciled</Badge>;
      case 'suspense':
        return <Badge className="bg-[#C85A40]/10 text-[#C85A40]">Suspense</Badge>;
      default:
        return <Badge className="bg-[#D99C3B]/10 text-[#D99C3B]">Unreconciled</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]">Bank Reconciliation</h1>
          <p className="text-[#5C635F] mt-1">Match bank deposits to student payments</p>
        </div>
        
        <div className="flex gap-2">
          {/* Excel Import */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleExcelUpload}
            accept=".xlsx,.xls,.csv"
            className="hidden"
          />
          {/* A statement file belongs to one branch. Locked users import into their
              own; an admin picks it here before choosing the file. */}
          {canReconcile && isAdmin && (
            <Select value={importBranch} onValueChange={setImportBranch}>
              <SelectTrigger className="w-40" data-testid="import-branch-select">
                <SelectValue placeholder="Import branch" />
              </SelectTrigger>
              <SelectContent>
                {(config?.branches || []).map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {canReconcile && (
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="import-excel-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
              Import Excel
            </Button>
          )}

          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" data-testid="add-bank-txn-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Transaction
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Bank Transaction</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddTransaction} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input type="date" value={newTxn.date} onChange={(e) => setNewTxn({ ...newTxn, date: e.target.value })} required data-testid="bank-txn-date" />
                </div>
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input type="number" value={newTxn.amount} onChange={(e) => setNewTxn({ ...newTxn, amount: e.target.value })} required min="0" data-testid="bank-txn-amount" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <CurrencySelector value={newTxn.currency} onChange={(v) => setNewTxn({ ...newTxn, currency: v })} />
                </div>
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <BankSelect value={newTxn.bank_name} onChange={(v) => setNewTxn({ ...newTxn, bank_name: v })} testId="bank-txn-bank" />
                </div>
              </div>
              {/* A statement line belongs to one branch. Branch-locked users get
                  their own automatically; an admin has to say which. */}
              {isAdmin && (
                <div className="space-y-2">
                  <Label>Branch</Label>
                  <Select value={newTxn.branch} onValueChange={(v) => setNewTxn({ ...newTxn, branch: v })}>
                    <SelectTrigger data-testid="bank-txn-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>
                      {(config?.branches || []).map((b) => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Reference Number</Label>
                <Input value={newTxn.reference_number} onChange={(e) => setNewTxn({ ...newTxn, reference_number: e.target.value })} required data-testid="bank-txn-ref" />
              </div>
              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Input value={newTxn.description} onChange={(e) => setNewTxn({ ...newTxn, description: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={submitting} data-testid="submit-bank-txn-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Add Transaction
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-[#D8D9E8]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#D99C3B]/10 rounded-lg">
                  <Clock className="w-5 h-5 text-[#D99C3B]" />
                </div>
                <div>
                  <p className="text-sm text-[#5C635F]">Unreconciled</p>
                  <p className="text-xl font-bold text-[#1C1F1D]">{summary.unreconciled?.count || 0}</p>
                  <p className="text-sm text-[#5C635F]">{formatCurrency(summary.unreconciled?.total || 0, 'USD')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#D8D9E8]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#C85A40]/10 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-[#C85A40]" />
                </div>
                <div>
                  <p className="text-sm text-[#5C635F]">Suspense</p>
                  <p className="text-xl font-bold text-[#1C1F1D]">{summary.suspense?.count || 0}</p>
                  <p className="text-sm text-[#5C635F]">{formatCurrency(summary.suspense?.total || 0, 'USD')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#D8D9E8]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#5556A8]/10 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-[#5556A8]" />
                </div>
                <div>
                  <p className="text-sm text-[#5C635F]">Reconciled</p>
                  <p className="text-xl font-bold text-[#1C1F1D]">{summary.reconciled?.count || 0}</p>
                  <p className="text-sm text-[#5C635F]">{formatCurrency(summary.reconciled?.total || 0, 'USD')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-4 flex flex-wrap gap-2">
          <MultiSelect widthClass="w-full sm:w-48" placeholder="All Status" testId="bank-status-filter"
            options={[{ value: 'unreconciled', label: 'Unreconciled' }, { value: 'reconciled', label: 'Reconciled' }, { value: 'suspense', label: 'Suspense' }]}
            selected={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); }} />
          {/* Branch-locked users already see only their own branch (enforced by the
              API), so the picker is only meaningful for admins. */}
          {isAdmin && (
            <MultiSelect widthClass="w-full sm:w-48" placeholder="All Branches" testId="bank-branch-filter"
              options={config?.branches || []}
              selected={branchFilter} onChange={(v) => { setBranchFilter(v); setPage(1); }} />
          )}
        </CardContent>
      </Card>

      {/* Floats over the page — selecting a row must not shift the table. */}
      {isAdmin && (
        <BulkSelectionBar count={sel.size} onClear={sel.clear}>
          <BulkDeleteButton count={sel.size} deleting={bulkDeleting} onClick={handleBulkDelete} label="transactions" />
        </BulkSelectionBar>
      )}

      {/* Transactions table */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#5C635F]">
              <p>No bank transactions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F5FA]">
                    {isAdmin && (
                      <TableHead className="w-8">
                        <Checkbox
                          checked={transactions.length > 0 && transactions.every((t) => sel.isSelected(t.id))}
                          onCheckedChange={() => sel.toggleAll(transactions.map((t) => t.id))}
                          aria-label="Select all"
                        />
                      </TableHead>
                    )}
                    <TableHead className="text-xs uppercase font-semibold">Date</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Reference</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Branch</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Bank</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-right">Amount</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Status</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((txn) => (
                    <TableRow key={txn.id}>
                      {isAdmin && (
                        <TableCell>
                          <Checkbox checked={sel.isSelected(txn.id)} onCheckedChange={() => sel.toggle(txn.id)} aria-label="Select row" />
                        </TableCell>
                      )}
                      <TableCell className="text-[#5C635F]">{formatDate(txn.date)}</TableCell>
                      <TableCell className="font-medium">{txn.reference_number}</TableCell>
                      <TableCell>
                        {txn.branch
                          ? <Badge variant="outline" className="text-xs">{txn.branch}</Badge>
                          : <span className="text-xs text-[#C85A40]">unassigned</span>}
                      </TableCell>
                      <TableCell>{txn.bank_name}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{formatCurrency(txn.amount, txn.currency)}</TableCell>
                      <TableCell>{getStatusBadge(txn.status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {canReconcile && txn.status === 'unreconciled' && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => openReconcileDialog(txn)} data-testid={`reconcile-${txn.id}`}>
                                <Link2 className="w-4 h-4 mr-1" /> Reconcile
                              </Button>
                              <Button variant="ghost" size="sm" className="text-[#C85A40]" onClick={() => handleMoveToSuspense(txn.id)} data-testid={`suspense-${txn.id}`}>
                                <AlertCircle className="w-4 h-4 mr-1" /> Suspense
                              </Button>
                            </>
                          )}
                          {canReconcile && txn.status === 'suspense' && (
                            <Button variant="ghost" size="sm" onClick={() => openReconcileDialog(txn)} data-testid={`reconcile-suspense-${txn.id}`}>
                              <Link2 className="w-4 h-4 mr-1" /> Match Student
                            </Button>
                          )}
                          {isAdmin && (
                            txn.tally_posted ? (
                              <Button variant="ghost" size="sm" disabled title="Posted to Tally - Locked">
                                <Lock className="w-4 h-4 text-[#999]" />
                              </Button>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={() => openBTEdit(txn)} title="Edit" data-testid={`edit-bank-txn-${txn.id}`}>
                                <Pencil className="w-4 h-4 text-[#3E3F96]" />
                              </Button>
                            )
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reconcile Dialog */}
      <Dialog open={showReconcileDialog} onOpenChange={(open) => { setShowReconcileDialog(open); if (!open) resetReconcileState(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reconcile Transaction</DialogTitle>
          </DialogHeader>
          {selectedTxn && (
            <div className="space-y-4">
              <div className="p-4 bg-[#F5F5FA] rounded-lg">
                <p className="text-sm text-[#5C635F]">Transaction Details</p>
                <p className="font-medium">{selectedTxn.reference_number}</p>
                <p className="text-lg font-bold text-[#3E3F96] tabular-nums">{formatCurrency(selectedTxn.amount, selectedTxn.currency)}</p>
                {selectedTxn.description && <p className="text-sm text-[#5C635F] mt-1">{selectedTxn.description}</p>}
              </div>

              {/* Mode Toggle */}
              <div className="flex gap-2">
                <Button variant={splitMode ? 'outline' : 'default'} size="sm" onClick={() => { setSplitMode(false); setSplits([]); }}
                  className={!splitMode ? 'bg-[#3E3F96] hover:bg-[#2D2E7A]' : ''} data-testid="single-mode-btn">
                  Single Student
                </Button>
                <Button variant={splitMode ? 'default' : 'outline'} size="sm" onClick={() => { setSplitMode(true); setSelectedStudent(null); }}
                  className={splitMode ? 'bg-[#3E3F96] hover:bg-[#2D2E7A]' : ''} data-testid="split-mode-btn">
                  Split Across Students
                </Button>
              </div>

              {splitMode ? (
                <>
                  {/* Split Mode */}
                  {splits.length > 0 && (
                    <div className="space-y-2">
                      {splits.map((split, idx) => (
                        <div key={split.student.id} className="flex items-center gap-2 p-2 bg-[#F5F5FA] rounded-lg">
                          <div className="flex-1">
                            <p className="text-sm font-medium">{split.student.first_name} {split.student.last_name}</p>
                            <p className="text-xs text-[#5C635F]">{split.student.student_id}</p>
                          </div>
                          <Input type="number" step="0.01" placeholder="Amount" value={split.amount} onChange={e => updateSplitAmount(idx, e.target.value)}
                            className="w-28" data-testid={`split-amount-${idx}`} />
                          <Button variant="ghost" size="sm" onClick={() => removeSplit(idx)} className="text-red-500">X</Button>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm px-2">
                        <span className="text-[#5C635F]">Split Total: <strong className="text-[#1C1F1D]">{formatCurrency(splitTotal, selectedTxn.currency)}</strong></span>
                        <span className={splitRemaining === 0 ? 'text-green-600' : 'text-[#C85A40]'}>
                          Remaining: <strong>{formatCurrency(splitRemaining, selectedTxn.currency)}</strong>
                        </span>
                      </div>
                    </div>
                  )}
                  {/* Search to add student */}
                  <div className="space-y-2">
                    <Label>Add Student</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5C635F]" />
                      <Input placeholder="Search student to add..." value={studentSearch}
                        onChange={(e) => { setStudentSearch(e.target.value); searchStudents(e.target.value); }}
                        className="pl-9" data-testid="split-student-search" />
                      {searchingStudent && <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin" />}
                      {studentResults.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-[#D8D9E8] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {studentResults.map(student => (
                            <button key={student.id} type="button" className="w-full px-3 py-2 text-left hover:bg-[#F5F5FA] flex items-center justify-between"
                              onClick={() => addSplit(student)}>
                              <span className="font-medium">{student.first_name} {student.last_name}</span>
                              <span className="text-sm text-[#5C635F]">{student.student_id}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Single Student Mode */}
                  {loadingSuggestions ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-[#3E3F96]" />
                      <span className="ml-2 text-sm text-[#5C635F]">Finding matches...</span>
                    </div>
                  ) : matchSuggestions.length > 0 && !selectedStudent && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-[#D99C3B]">
                        <Lightbulb className="w-4 h-4" />
                        <span className="text-sm font-medium">Suggested Matches</span>
                      </div>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {matchSuggestions.map((suggestion, idx) => (
                          <button key={idx} className="w-full p-3 text-left border border-[#D8D9E8] rounded-lg hover:bg-[#F5F5FA] transition-colors"
                            onClick={() => handleStudentSelect(suggestion.student)}>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{suggestion.student.first_name} {suggestion.student.last_name}</p>
                                <p className="text-xs text-[#5C635F]">{suggestion.student.student_id}</p>
                              </div>
                              <Badge variant="outline" className={
                                suggestion.confidence === 'high' ? 'bg-[#5556A8]/10 text-[#5556A8]' :
                                suggestion.confidence === 'medium' ? 'bg-[#D99C3B]/10 text-[#D99C3B]' : 'bg-gray-100 text-gray-600'
                              }>{suggestion.confidence}</Badge>
                            </div>
                            <p className="text-xs text-[#5C635F] mt-1">{suggestion.match_reason}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Match to Student</Label>
                    {selectedStudent ? (
                      <div className="flex items-center justify-between p-3 bg-[#F5F5FA] rounded-lg">
                        <div>
                          <p className="font-medium">{selectedStudent.first_name} {selectedStudent.last_name}</p>
                          <p className="text-sm text-[#5C635F]">{selectedStudent.student_id}</p>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedStudent(null)}>Change</Button>
                      </div>
                    ) : (
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5C635F]" />
                        <Input placeholder="Search student..." value={studentSearch}
                          onChange={(e) => { setStudentSearch(e.target.value); searchStudents(e.target.value); }}
                          className="pl-9" data-testid="reconcile-student-search" />
                        {searchingStudent && <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-[#5C635F]" />}
                        {studentResults.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-[#D8D9E8] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {studentResults.map(student => (
                              <button key={student.id} type="button" className="w-full px-3 py-2 text-left hover:bg-[#F5F5FA] flex items-center justify-between"
                                onClick={() => handleStudentSelect(student)}>
                                <span className="font-medium">{student.first_name} {student.last_name}</span>
                                <span className="text-sm text-[#5C635F]">{student.student_id}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowReconcileDialog(false); resetReconcileState(); }}>Cancel</Button>
                <Button onClick={handleReconcile} className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
                  disabled={submitting || (splitMode ? splits.length === 0 || Math.abs(splitRemaining) > 0.01 : !selectedStudent)}
                  data-testid="confirm-reconcile-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {splitMode ? `Split & Reconcile (${splits.length})` : 'Reconcile & Record Payment'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#5C635F]">Page {page} of {totalPages}</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Bank Transaction Dialog */}
      <Dialog open={!!editBT} onOpenChange={(open) => { if (!open) { setEditBT(null); setBtPayments([]); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Bank Transaction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={editBTData.date || ''} onChange={(e) => setEditBTData({ ...editBTData, date: e.target.value })} data-testid="edit-bt-date" />
              </div>
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input type="number" value={editBTData.amount || ''} onChange={(e) => setEditBTData({ ...editBTData, amount: parseFloat(e.target.value) })} min="0" step="0.01" data-testid="edit-bt-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Reference #</Label>
                <Input value={editBTData.reference_number || ''} onChange={(e) => setEditBTData({ ...editBTData, reference_number: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Bank Name</Label>
                <BankSelect value={editBTData.bank_name || ''} onChange={(v) => setEditBTData({ ...editBTData, bank_name: v })} testId="edit-bank-txn-bank" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={editBTData.currency} onValueChange={(v) => setEditBTData({ ...editBTData, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="UGX">UGX</SelectItem>
                  <SelectItem value="RWF">RWF</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={editBTData.description || ''} onChange={(e) => setEditBTData({ ...editBTData, description: e.target.value })} />
            </div>

            {/* Invoice mapping of the payment(s) this line was reconciled into.
                Unticking releases the invoice (outstanding again, free for another
                payment); ticking another marks the same money against it instead. */}
            {editBT?.status !== 'reconciled' ? (
              <p className="text-xs text-[#5C635F]" data-testid="bt-mapping-unreconciled">
                Not reconciled yet — match this transaction to a student first, then its
                invoice mapping can be changed here.
              </p>
            ) : btPaymentsLoading ? (
              <div className="flex items-center gap-2 text-sm text-[#5C635F]">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading invoice mapping...
              </div>
            ) : btPayments.length === 0 ? (
              <p className="text-xs text-[#C85A40]" data-testid="bt-mapping-not-found">
                No payment could be traced to this reconciled transaction, so there is no
                invoice mapping to change here. Edit the payment directly on the Payments page.
              </p>
            ) : (
              <div className="space-y-3">
                {btPayments.map((m) => (
                  <InvoiceMappingEditor
                    key={m.payment.id}
                    paymentId={m.payment.id}
                    canEdit={isAdmin && !m.payment.tally_posted}
                    onSaved={() => { fetchTransactions(); fetchSummary(); }}
                    title={`Invoice Mapping — ${m.payment.student_name || 'Payment'}`}
                  />
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditBT(null)}>Cancel</Button>
              <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" onClick={handleEditBTSave} disabled={editBTSubmitting} data-testid="save-edit-bt">
                {editBTSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankReconciliationPage;
